// ============================================================
// Personas — source de vérité (Sprint 8, 2026-05-26)
//
// Cf. Grille de Personas Unifiée v1.0 (mai 2026).
// 5 personas canon. Slugs alignés sur le quiz ExpatPersonaQuiz pour
// éviter la dérive entre /data/personas.js (funnel) et /data/expatPersonas.js
// (quiz). Plus de doublons "expat-soon", "curious" — ces deux-là
// vivent désormais sur l'axe `intent` (cf. intentTags.js) et "antoine
// chiens" vit comme tag transversal pet_owner.
// ============================================================

/**
 * @typedef {'retraite'|'entrepreneur'|'nomad'|'famille'|'etudiant'} PersonaSlug
 */

/**
 * @typedef {object} Persona
 * @property {PersonaSlug} slug
 * @property {string} emoji
 * @property {'gold'|'navy'|'tool'|'bordeaux'|'cream'} accent  Tailwind color family
 * @property {string} accentHex                                 CSS resolved value for inline use
 * @property {('fr'|'en'|'es')[]} languages                     Ordered : dominant first
 * @property {{fr: string, en: string, es: string}} label
 * @property {{fr: string, en: string, es: string}} shortDesc
 * @property {{fr: string, en: string, es: string}} startHereTeaser  CTA copy on Start Here card
 * @property {string} flagshipProduct                            Product to push primarily
 * @property {string[]} affiliatePriority                        Ordered list of affiliate slugs
 * @property {number} ltvRank                                    1 = highest LTV
 * @property {string} ctaTone                                    posed | business | direct | practical | narrative
 */

export const PERSONAS = {
  retraite: {
    slug: 'retraite',
    emoji: '🌴',
    accent: 'tool',
    accentHex: '#0F5350',      // Océan profond (rebrand 2026) — ex-or #A88536
    languages: ['fr', 'en'],
    label: { fr: 'Retraité', en: 'Retiree', es: 'Jubilado' },
    shortDesc: {
      fr: 'Couple 55+, capital constitué, recherche douceur fiscale et climat.',
      en: 'Couple 55+, capital saved, looking for tax softness and climate.',
      es: 'Pareja 55+, capital constituido, buscando suavidad fiscal y clima.',
    },
    startHereTeaser: {
      fr: 'Je prépare ma retraite au soleil.',
      en: 'I\'m planning my retirement in the sun.',
      es: 'Estoy planeando mi jubilación al sol.',
    },
    flagshipProduct: 'email-audit-99',
    affiliatePriority: ['cigna-global', 'wise', 'b2b-avocat-pensionado'],
    ltvRank: 4,
    ctaTone: 'posed',
  },
  entrepreneur: {
    slug: 'entrepreneur',
    emoji: '🚀',
    accent: 'navy',
    accentHex: '#23201A',      // Sombre/encre (rebrand) — ex-navy #0A1428
    languages: ['es', 'en'],
    label: { fr: 'Entrepreneur', en: 'Entrepreneur', es: 'Emprendedor' },
    shortDesc: {
      fr: 'Crée ou délocalise sa société. Optimisation fiscale et banking.',
      en: 'Building or relocating a business. Tax planning and banking.',
      es: 'Crea o relocaliza su empresa. Optimización fiscal y banking.',
    },
    startHereTeaser: {
      fr: 'Je crée ma société au Panama.',
      en: 'I\'m setting up a company in Panama.',
      es: 'Estoy creando mi empresa en Panamá.',
    },
    flagshipProduct: 'email-pack-149',
    affiliatePriority: ['b2b-avocat-corp', 'wise', 'tower-bank'],
    ltvRank: 2,
    ctaTone: 'business',
  },
  nomad: {
    slug: 'nomad',
    emoji: '💻',
    accent: 'tool',          // Océan (rebrand 2026) — ex-bleu #1F4980
    accentHex: '#17726F',
    languages: ['en', 'fr'],
    label: { fr: 'Nomade digital', en: 'Digital nomad', es: 'Nómada digital' },
    shortDesc: {
      fr: 'Revenus en ligne, fiscalité territoriale, vie coworking + voyage.',
      en: 'Online income, territorial tax, coworking + travel lifestyle.',
      es: 'Ingresos online, fiscalidad territorial, coworking + viaje.',
    },
    startHereTeaser: {
      fr: 'Je télétravaille — Panama est mon hub.',
      en: 'I work online — Panama is my hub.',
      es: 'Trabajo en línea — Panamá es mi hub.',
    },
    flagshipProduct: 'email-course-97',
    affiliatePriority: ['nordvpn', 'wise', 'safetywing', 'selina-coworking'],
    ltvRank: 3,
    ctaTone: 'direct',
  },
  famille: {
    slug: 'famille',
    emoji: '👨‍👩‍👧',
    accent: 'bordeaux',
    accentHex: '#9C4628',      // Rouille foncé (rebrand) — ex-bordeaux #7E2723
    languages: ['fr', 'en'],
    label: { fr: 'Famille', en: 'Family', es: 'Familia' },
    shortDesc: {
      fr: 'Avec enfants. Écoles, sécurité, assurance santé famille.',
      en: 'With kids. Schools, safety, family health insurance.',
      es: 'Con hijos. Escuelas, seguridad, seguro de salud familiar.',
    },
    startHereTeaser: {
      fr: 'Je viens en famille avec mes enfants.',
      en: 'I\'m moving with my family.',
      es: 'Vengo con mi familia.',
    },
    flagshipProduct: 'toolkit-39',
    affiliatePriority: ['cigna-famille', 'immobilier-clayton', 'booking'],
    ltvRank: 5,  // à confirmer
    ctaTone: 'practical',
  },
  etudiant: {
    slug: 'etudiant',
    emoji: '🎒',
    accent: 'ink',            // entrée douce — taupe sourd, pas d'accent fort
    accentHex: '#6B6253',      // Muted (rebrand) — ex-crème #E8DFC4 (illisible en texte)
    languages: ['fr', 'en', 'es'],
    label: { fr: 'Explorateur', en: 'Explorer', es: 'Explorador' },
    shortDesc: {
      fr: 'Curieux. Étudiant, jeune actif. Découverte sensorielle d\'abord.',
      en: 'Curious. Student or young pro. Sensory discovery first.',
      es: 'Curioso. Estudiante o joven profesional. Descubrimiento primero.',
    },
    startHereTeaser: {
      fr: 'Je découvre, je ne planifie pas (encore).',
      en: 'I\'m just exploring, not planning yet.',
      es: 'Solo exploro, todavía no planeo.',
    },
    flagshipProduct: 'lead-magnet',
    affiliatePriority: [],  // pas de push affilié
    ltvRank: 6,
    ctaTone: 'narrative',
  },
};

export const PERSONA_SLUGS = ['retraite', 'entrepreneur', 'nomad', 'famille', 'etudiant'];

/** Type guard / canonicalizer.
 *  Translates legacy slugs (expat-soon, curious, retiree) to canon. */
export function canonicalPersonaSlug(input) {
  if (!input) return null;
  const map = {
    'retiree': 'retraite',
    'retraité': 'retraite',
    'family': 'famille',
    'digital-nomad': 'nomad',
    'student': 'etudiant',
    'curious': null,        // → intent.exploring
    'expat-soon': null,     // → intent.move-soon
  };
  const norm = input.toLowerCase().trim();
  if (PERSONA_SLUGS.includes(norm)) return norm;
  if (norm in map) return map[norm];
  return null;
}

export function getPersona(slug) {
  return PERSONAS[canonicalPersonaSlug(slug)] || null;
}
